import { Link } from 'react-router-dom';
import type { BlogPost } from '../../../mocks/blog';

interface AuthorArticlesProps {
  posts: BlogPost[];
  authorName: string;
}

export default function AuthorArticles({ posts, authorName }: AuthorArticlesProps) {
  if (posts.length === 0) {
    return (
      <section className="py-16 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 text-center py-20 text-zinc-600">
          <i className="ri-file-text-line text-4xl mb-3 block" />
          <p>Zatiaľ žiadne články od tohto autora.</p>
        </div>
      </section>
    );
  }

  const [featured, ...rest] = posts;

  return (
    <section className="py-16 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-2xl font-black text-white">
              Články od autora
              <span className="text-orange-400 ml-2 text-base font-semibold">({posts.length})</span>
            </h2>
            <p className="text-zinc-500 text-sm mt-1">Všetky publikované príspevky</p>
          </div>
          <Link
            to="/blog"
            className="hidden sm:inline-flex items-center gap-2 text-zinc-500 hover:text-white text-sm transition-colors duration-200 cursor-pointer"
          >
            Všetky články
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Topics */}
        <div className="flex flex-wrap gap-2 mb-10">
          {Array.from(new Set(posts.map((p) => p.category))).map((cat) => (
            <span
              key={cat}
              className="text-xs text-zinc-400 bg-zinc-800/70 border border-zinc-700/50 px-3 py-1.5 rounded-full"
            >
              {cat}
            </span>
          ))}
        </div>

        {/* Featured */}
        <Link
          to={`/blog/${featured.id}`}
          className="group block mb-8 bg-zinc-900 border border-zinc-800/60 rounded-2xl overflow-hidden hover:border-orange-500/30 transition-all duration-300 cursor-pointer"
        >
          <div className="grid md:grid-cols-5">
            <div className="md:col-span-2 aspect-video md:aspect-auto overflow-hidden">
              <img
                src={featured.image}
                alt={featured.title}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="md:col-span-3 p-7 lg:p-9 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className={`text-xs font-semibold rounded-full px-3 py-1 ${featured.categoryColor}`}>
                    {featured.category}
                  </span>
                  <span className="bg-orange-500/10 text-orange-400 text-xs font-semibold rounded-full px-3 py-1 border border-orange-500/20">
                    Odporúčané
                  </span>
                </div>
                <h3 className="text-xl lg:text-2xl font-black text-white leading-snug mb-3 group-hover:text-orange-300 transition-colors duration-200">
                  {featured.title}
                </h3>
                <p className="text-zinc-500 text-sm leading-relaxed line-clamp-2">{featured.excerpt}</p>
              </div>
              <div className="flex items-center justify-between mt-5 pt-5 border-t border-zinc-800/60">
                <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
                  <i className="ri-calendar-line text-orange-400" />
                  {featured.date}
                </div>
                <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
                  <i className="ri-time-line text-orange-400" />
                  {featured.readTime}
                </div>
                <span className="text-orange-400 text-sm font-semibold group-hover:translate-x-1 transition-transform duration-200 inline-flex items-center gap-1">
                  Čítať <i className="ri-arrow-right-line" />
                </span>
              </div>
            </div>
          </div>
        </Link>

        {/* Grid of rest */}
        {rest.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                className="group flex flex-col bg-zinc-900 border border-zinc-800/60 rounded-2xl overflow-hidden hover:border-orange-500/30 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="flex flex-col flex-1 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-xs font-semibold rounded-full px-3 py-1 ${post.categoryColor}`}>
                      {post.category}
                    </span>
                    <span className="text-zinc-600 text-xs flex items-center gap-1">
                      <i className="ri-time-line" />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="text-white font-bold text-sm leading-snug mb-3 group-hover:text-orange-300 transition-colors duration-200 flex-1 line-clamp-3">
                    {post.title}
                  </h3>
                  <div className="flex items-center justify-between pt-4 border-t border-zinc-800/60 mt-auto">
                    <span className="text-zinc-600 text-xs">{post.date}</span>
                    <span className="text-orange-400 text-xs font-semibold flex items-center gap-1 group-hover:gap-2 transition-all duration-200">
                      Čítať <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
